import { motion } from "framer-motion";

/**
 * Shared abstract node/network visual — the signature Al Yakhour aesthetic.
 * Pure SVG + framer-motion (crisp at any scale, brand-driven, no stock imagery).
 * variant controls the node layout so each section feels related but distinct.
 */
const LAYOUTS = {
  agent: {
    nodes: [
      [60, 90], [180, 55], [300, 110], [120, 205],
      [255, 215], [200, 135], [325, 60], [70, 190],
    ],
    edges: [[5, 0], [5, 1], [5, 2], [5, 3], [5, 4], [0, 3], [1, 2], [3, 4], [1, 6], [0, 7]],
    center: 5,
  },
  media: {
    nodes: [
      [70, 70], [190, 50], [305, 95], [110, 180],
      [245, 190], [180, 125], [60, 225], [318, 210],
    ],
    edges: [[5, 0], [5, 1], [5, 3], [5, 4], [0, 6], [1, 2], [2, 7], [4, 7], [3, 6], [5, 2]],
    center: 5,
  },
  capabilities: {
    nodes: [
      [55, 130], [160, 60], [160, 210], [265, 95],
      [265, 185], [335, 140], [160, 135],
    ],
    edges: [[6, 0], [6, 1], [6, 2], [6, 3], [6, 4], [3, 5], [4, 5], [1, 3], [2, 4]],
    center: 6,
  },
  ventures: {
    nodes: [
      [80, 85], [205, 60], [305, 120], [140, 200], [265, 215], [190, 135],
    ],
    edges: [[5, 0], [5, 1], [5, 2], [5, 3], [5, 4], [0, 3], [1, 2]],
    center: 5,
  },
};

export default function NetworkGraphic({ variant = "agent", label, badge, className = "" }) {
  const layout = LAYOUTS[variant] || LAYOUTS.agent;
  const { nodes, edges, center } = layout;
  const c = nodes[center];
  const target = nodes[0];

  return (
    <div
      className={`relative overflow-hidden rounded-sm border border-hairline bg-surface ${className}`}
      data-testid={`network-graphic-${variant}`}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_62%_38%,rgba(100,133,165,0.24),transparent_62%)]" />
      <div className="pointer-events-none absolute -bottom-10 -left-10 h-44 w-44 rounded-full bg-steel/20 blur-[70px]" />

      <svg viewBox="0 0 380 280" className="relative h-full w-full p-5" preserveAspectRatio="xMidYMid meet">
        {edges.map(([a, b], i) => (
          <motion.line
            key={`e${i}`}
            x1={nodes[a][0]}
            y1={nodes[a][1]}
            x2={nodes[b][0]}
            y2={nodes[b][1]}
            stroke="#4A6984"
            strokeWidth="1"
            initial={{ pathLength: 0, opacity: 0 }}
            whileInView={{ pathLength: 1, opacity: 0.55 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, delay: 0.15 + i * 0.07, ease: "easeOut" }}
          />
        ))}

        {/* Travelling data packet */}
        <motion.circle
          r="3"
          fill="#8FB4D6"
          initial={{ opacity: 0 }}
          animate={{
            cx: [c[0], target[0], c[0]],
            cy: [c[1], target[1], c[1]],
            opacity: [0, 1, 0],
          }}
          transition={{ duration: 2.6, repeat: Infinity, repeatDelay: 0.5, ease: "easeInOut" }}
        />

        {nodes.map((n, i) => (
          <motion.circle
            key={`n${i}`}
            cx={n[0]}
            cy={n[1]}
            r={i === center ? 8 : 5}
            fill={i === center ? "#6485A5" : "#0D1326"}
            stroke="#6485A5"
            strokeWidth="1.5"
            initial={{ scale: 0, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 + i * 0.08 }}
            style={{ transformOrigin: `${n[0]}px ${n[1]}px` }}
          />
        ))}

        {/* Pulsing ring on the hub */}
        <motion.circle
          cx={c[0]}
          cy={c[1]}
          r="8"
          fill="none"
          stroke="#6485A5"
          strokeWidth="1"
          animate={{ opacity: [0.55, 0], scale: [1, 2.6] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }}
          style={{ transformOrigin: `${c[0]}px ${c[1]}px` }}
        />
      </svg>

      {(label || badge) && (
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between border-t border-hairline bg-ink/50 px-5 py-4 backdrop-blur-sm">
          {label && (
            <span className="font-body text-xs uppercase tracking-[0.22em] text-fog">{label}</span>
          )}
          {badge && (
            <span className="flex items-center gap-2 font-body text-xs text-steel-light">
              <span className="h-2 w-2 animate-pulse rounded-full bg-steel-light" />
              {badge}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
